import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  LinearProgress,
  Chip,
  Snackbar
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Rocket as RocketIcon
} from '@mui/icons-material';
import { apiClient as api } from '../../api';

interface Question {
  question_text: string;
}

interface Rule {
  rule_name: string;
  action_type: string;
}

interface DeployStepProps {
  tenantId?: number;
  questions: Question[];
  rules: Rule[];
  testResults: any;
  onComplete: () => void;
  onBack: () => void;
}

const DeployStep: React.FC<DeployStepProps> = ({
  tenantId,
  questions,
  rules,
  testResults,
  onComplete,
  onBack
}) => {
  const [deploying, setDeploying] = useState(false);
  const [deployed, setDeployed] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Helper function to show notifications
  const showNotification = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleDeploy = async () => {
    setDeploying(true);

    try {
      // Deploy configuration to backend
      const response = await api.post('/api/risk-scoping/deploy', {
        tenant_id: tenantId,
        questions,
        rules,
        test_results: testResults
      });

      console.log('✅ Deployment response:', response.data);

      setDeployed(true);
      showNotification('Risk Scoping configuration saved successfully!', 'success');
      setTimeout(() => {
        onComplete();
      }, 1500);
    } catch (err: any) {
      console.error('❌ Deployment failed:', err);
      showNotification('Deployment failed: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setDeploying(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Review & Deploy
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Review your configuration and deploy it to production.
        </Typography>
      </Box>

      {/* Configuration Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Configuration Summary
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Questions Defined
            </Typography>
            <List dense>
              {questions.map((q, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText primary={q.question_text} />
                </ListItem>
              ))}
            </List>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Rules Configured
            </Typography>
            <List dense>
              {rules.map((r, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={r.rule_name}
                    secondary={`Action: ${r.action_type}`}
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Test Results
            </Typography>
            {testResults && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={`${testResults.statistics?.totalRisks || 0} Total Risks`}
                  color="default"
                />
                <Chip
                  label={`${testResults.statistics?.applicableRisks || 0} Applicable`}
                  color="success"
                />
                <Chip
                  label={`${testResults.statistics?.excludedRisks || 0} Excluded (${testResults.statistics?.reductionPercentage || 0}%)`}
                  color="error"
                />
                <Chip
                  label={`${testResults.appliedRules?.length || 0} Rules Applied`}
                  color="primary"
                />
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Pre-Deployment Checklist */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Pre-Deployment Checklist
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary="Questions Validated"
                secondary={`${questions.length} questions configured and ready`}
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary="Rules Validated"
                secondary={`${rules.length} rules configured and ready`}
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary="Configuration Tested"
                secondary="Test scenarios executed successfully"
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary="Impact Analyzed"
                secondary={`Expected ${testResults?.statistics?.reductionPercentage || 0}% reduction in risk assessment burden`}
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Deployment Impact Warning */}
      <Alert severity="info" icon={<WarningIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          Deployment Impact
        </Typography>
        <Typography variant="body2">
          • This configuration will be applied to all new risk assessments<br />
          • Existing assessments will not be affected<br />
          • You can modify or disable rules at any time<br />
          • Changes can be rolled back if needed
        </Typography>
      </Alert>

      {deploying && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" gutterBottom>
            Deploying configuration...
          </Typography>
          <LinearProgress />
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={onBack} disabled={deploying || deployed}>
          ← Back to Testing
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<RocketIcon />}
          onClick={handleDeploy}
          disabled={deploying || deployed}
        >
          {deploying ? 'Deploying...' : deployed ? 'Deployed!' : 'Deploy Configuration'}
        </Button>
      </Box>

      {/* Toast Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeployStep;
